'use client'

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'

interface SidebarContextType {
  isCollapsed: boolean
  toggleSidebar: () => void
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined)

// Helper to get initial state synchronously from localStorage
// This prevents flash on mount/remount
function getInitialCollapsedState(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const saved = localStorage.getItem('holescale-sidebar-collapsed');
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    // Silently fail - use default
  }
  return false;
}

export function SidebarProvider({ children }: { children: ReactNode }) {
  // Initialize from localStorage synchronously to prevent flash
  const [isCollapsed, setIsCollapsed] = useState(getInitialCollapsedState)

  // Memoize toggleSidebar to prevent stale closure issues
  const toggleSidebar = useCallback(() => {
    setIsCollapsed(prev => {
      const newState = !prev
      localStorage.setItem('holescale-sidebar-collapsed', JSON.stringify(newState))
      return newState
    })
  }, [])

  // Keyboard shortcut (Cmd/Ctrl + B)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault()
        toggleSidebar()
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [toggleSidebar])

  return (
    <SidebarContext.Provider value={{ isCollapsed, toggleSidebar }}>
      {children}
    </SidebarContext.Provider>
  )
}

export function useSidebar() {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within SidebarProvider')
  }
  return context
}

